import React, { useRef, useEffect, useCallback } from 'react';
import { useDarkMode } from '@/hooks/useDarkMode';

interface WaveformCanvasProps {
    peaksCache: Map<string, {
        startTime: number;
        endTime: number;
        peaks: number[];
    }>;
    duration: number;
    pixelsPerSecond: number;
    currentTime: number;
    onTimeSeek: (time: number) => void;
    width: number;
    height?: number;
    scrollLeft: number;
    className?: string;
    style?: React.CSSProperties;
    subtitleRegions?: Array<{startTime: number, endTime: number}>;
    bufferRatio?: number;
}

export default function WaveformCanvas({
    peaksCache,
    duration,
    pixelsPerSecond,
    currentTime,
    onTimeSeek,
    width,
    height = 45,
    scrollLeft,
    className = '',
    style,
    subtitleRegions = [],
    bufferRatio = 0.5
}: WaveformCanvasProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const isDark = useDarkMode();

    const timeToX = useCallback((time: number) => time * pixelsPerSecond, [pixelsPerSecond]);
    const xToTime = useCallback((x: number) => x / pixelsPerSecond, [pixelsPerSecond]);

    const isUnderSubtitle = useCallback((time: number) => {
        return subtitleRegions.some(region =>
            time >= region.startTime && time <= region.endTime
        );
    }, [subtitleRegions]);

    const drawWaveform = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            console.log('WaveformCanvas: No canvas ref');
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            console.log('WaveformCanvas: No canvas context');
            return;
        }

        // Calculate buffered canvas dimensions
        const bufferWidth = width * bufferRatio;
        const canvasWidth = width + (bufferWidth * 2);
        const bufferOffset = bufferWidth;

        ctx.clearRect(0, 0, canvasWidth, height);

        canvas.width = canvasWidth;
        canvas.height = height;

        // Calculate visible area including buffer
        const bufferedScrollLeft = scrollLeft - bufferWidth;
        const visibleStartTime = xToTime(bufferedScrollLeft);
        const visibleEndTime = xToTime(bufferedScrollLeft + canvasWidth);
        const sortedSegments = Array.from(peaksCache.values()).sort((a, b) => a.startTime - b.startTime);

        const relevantSegments = sortedSegments.filter(segment =>
            segment.endTime > visibleStartTime && segment.startTime < visibleEndTime
        );

        const drawnPositions = new Set<number>();

        for (const segment of relevantSegments) {

            const segmentStartX = timeToX(segment.startTime);
            const segmentEndX = timeToX(segment.endTime);

            const viewportStartX = segmentStartX - bufferedScrollLeft;
            const viewportEndX = segmentEndX - bufferedScrollLeft;

            const visibleStartX = Math.max(0, viewportStartX);
            const visibleEndX = Math.min(canvasWidth, viewportEndX);

            if (visibleStartX >= visibleEndX) continue;

            const maxPeak = segment.peaks.reduce((max, value) => Math.max(max, Math.abs(value)), 0);
            const vScale = maxPeak ? 1 / maxPeak : 1;

            const segmentWidth = segmentEndX - segmentStartX;
            const peaksPerPixel = segment.peaks.length / segmentWidth;

            const absoluteStartX = Math.max(segmentStartX, bufferedScrollLeft);
            const absoluteEndX = Math.min(segmentEndX, bufferedScrollLeft + canvasWidth);

            const firstAbsoluteBarX = Math.ceil(absoluteStartX / 3) * 3;

            for (let absoluteX = firstAbsoluteBarX; absoluteX < absoluteEndX; absoluteX += 3) {

                if (drawnPositions.has(absoluteX)) continue;

                const viewportX = absoluteX - bufferedScrollLeft;
                if (viewportX < 0 || viewportX + 2 > canvasWidth) continue;

                const segmentRelativeX = absoluteX - segmentStartX;
                const peakIndex = Math.floor(segmentRelativeX * peaksPerPixel);

                if (peakIndex >= 0 && peakIndex < segment.peaks.length) {
                    const peakValue = segment.peaks[peakIndex];

                    const normalizedValue = Math.abs(peakValue) * vScale;
                    const barHeight = normalizedValue * height;

                    if (barHeight > 0) {
                        const barTime = xToTime(absoluteX);
                        const underSubtitle = isUnderSubtitle(barTime);

                        ctx.fillStyle = underSubtitle
                            ? (isDark ? 'rgba(96, 165, 250, 1)' : 'rgba(37, 99, 235, 1)')  // Darker blue for bars under subtitles
                            : (isDark ? 'rgba(147, 197, 253, 0.8)' : 'rgba(59, 130, 246, 1)'); // Original color for other bars

                        ctx.fillRect(viewportX, height - barHeight, 2, barHeight);

                        drawnPositions.add(absoluteX);
                    }
                }
            }
        }
    }, [peaksCache, duration, pixelsPerSecond, currentTime, width, height, scrollLeft, timeToX, xToTime, isUnderSubtitle, bufferRatio, isDark]);

    const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const canvasX = e.clientX - rect.left;
        const bufferWidth = width * bufferRatio;
        const viewportX = canvasX - bufferWidth;
        const absoluteX = viewportX + scrollLeft;
        const time = xToTime(absoluteX);

        onTimeSeek(Math.max(0, Math.min(time, duration)));
    }, [xToTime, onTimeSeek, duration, scrollLeft, width, bufferRatio]);

    useEffect(() => {
        drawWaveform();
    }, [drawWaveform]);

    const bufferWidth = width * bufferRatio;
    const canvasWidth = width + (bufferWidth * 2);

    return (
        <canvas
            ref={canvasRef}
            width={canvasWidth}
            height={height}
            className={`cursor-pointer ${className}`}
            onClick={handleCanvasClick}
            style={{
                width: `${canvasWidth}px`,
                height: `${height}px`,
                transform: `translateX(-${bufferWidth}px)`,
                ...style
            }}
        />
    );
}
